/**
 * Chat REST client (issue #351).
 *
 * Thin wrapper over the shared ``api`` axios instance. Targets the
 * backend ``/api/v1/chat/*`` surface landed by subtask 02. Used by
 * ``useChatQueries`` for the floating widget and by
 * ``AdminMessagesPage`` for the admin tool.
 */
import api from './api';

export type ChatAuthorKind = 'user' | 'admin' | 'agent';
export type ChatReaderKind = 'user' | 'admin';
export type ChatThreadKind =
  | 'human_support'
  | 'agent_navigator'
  | 'agent_reader'
  | 'agent_assistant';

export interface ChatThread {
  thread_id: string;
  tenant_id: string;
  user_id: string;
  thread_kind: ChatThreadKind;
  created_at: string;
  last_message_at: string;
  // Per-user unread count from the server-side LEFT JOIN. Always
  // present on list responses; ``ensure_thread`` single-row path
  // omits it (defaults to 0 in the consumer).
  unread_count: number;
  /** Conversation title (FR-015 agent workspace). ``null`` until the first
   * turn auto-titles it or the user renames it; absent on pre-FR-015
   * backends. Only ``agent_assistant`` threads carry one. */
  title?: string | null;
  /** Owning project (FR-015). ``null``/absent = ungrouped. */
  project_id?: string | null;
}

/**
 * A conversation project (FR-015 agent workspace) — a user-scoped folder for
 * ``agent_assistant`` conversations, level 1 in the /agent left panel.
 */
export interface ChatProject {
  project_id: string;
  tenant_id: string;
  user_id: string;
  name: string;
  created_at: string;
  deleted_at: string | null;
  /** Count of non-deleted conversations inside the project. */
  conversation_count: number;
}

export interface AdminChatThread extends ChatThread {
  last_body: string | null;
  last_sent_at: string | null;
}

/**
 * One cited-document badge on a KI-Hofassistent agent message (#1757).
 *
 * Only ``document_id`` is required. ``document_number`` (Belegnummer) is
 * nullable — a document may have no number, in which case the badge falls
 * back to a type+date label (subtask 07). ``document_type`` /
 * ``document_date`` may also be absent. Nothing produces this yet
 * (subtask 06 does) and nothing renders it yet (subtask 07 does).
 */
export interface DocumentSource {
  document_id: string;
  document_number?: string | null;
  document_type?: string | null;
  document_date?: string | null;
  /** Taxonomy-v2 role display label (#1930, forward-only): preferred over the
   * ``document_type`` snapshot when present. Old stored snapshots never carry
   * it and keep rendering ``document_type`` unchanged. */
  role_name?: string | null;
}

export interface ChatMessage {
  message_id: string;
  thread_id: string;
  tenant_id: string;
  author_kind: ChatAuthorKind;
  author_id: string;
  /** null when the message is soft-deleted (tombstone). */
  body: string | null;
  sent_at: string;
  source_url: string | null;
  deleted_at: string | null;
  deleted_by_admin_id: string | null;
  /** Backend-resolved display name for the author (User.first_name + last_name).
   * ``null`` for the agent kind or when the row could not be resolved. */
  author_display_name?: string | null;
  /** When the user (``reader_kind='user'``) marked this message read.
   * Drives the admin-side WhatsApp-style read tick on admin-authored
   * bubbles. ``null`` when not yet read; field omitted entirely on
   * pre-receipt backends so a missing key is treated as "unknown". */
  read_by_user_at?: string | null;
  /** Cited-document badges (#1757). Populated only on ``agent``
   * messages of the KI-Hofassistent (``agent_assistant``) thread. The
   * UDB ``MessageOut`` serialises the column as ``null`` (not absent)
   * for every existing row and every non-agent message, so the wire
   * value is ``null``/``undefined`` there — model both, matching the
   * sibling nullable fields above. Survives the SSE-invalidate →
   * refetch because it round-trips through UDB. */
  sources?: DocumentSource[] | null;
  /** Durable terminal-failure marker (#1834). A typed reason
   * (``llm_transport`` / ``internal``) stamped on a USER message whose
   * KI-Hofassistent turn terminally failed with NO persisted error
   * bubble. ``null``/absent on every other row. On a tab remount the tab
   * re-derives the failed/retry affordance from it — so the no-bubble
   * failure path no longer shows a phantom ≤120s "Thinking…" spinner that
   * the client safety timer would mislabel as ``reason:'timeout'``. */
  failed_reason?: string | null;
}

/**
 * KI-Hofassistent budget snapshot (issue #1656). Server-authoritative,
 * numbers already rounded to cents by ``GET /v1/assistant/budget``.
 */
export interface AssistantBudget {
  remaining_eur: number;
  monthly_limit_eur: number;
  currency: string;
  period: string;
}

/**
 * Response of a single assistant turn (``POST /v1/assistant/turn``). Since the
 * async-202 cutover (#1802) the normal path is:
 * - ``202 {status:'accepted', message:<persisted USER ChatMessage>}`` — the turn
 *   ran to the background; the reply (or a localized error bubble) is delivered
 *   later over ``chat:message.new`` SSE. ``budget`` is omitted (the debit
 *   happens in the background task).
 * - ``200 {status:'blocked', message, budget}`` — the balance is exhausted and
 *   ``message`` is a localized "wait / contact support" agent bubble.
 *
 * ``'ok'`` (legacy synchronous reply) and ``'error'`` (transitional persisted
 * error bubble) are still modelled so a mixed-version backend never surprises
 * the discriminator. ``budget`` is therefore optional.
 */
export interface AssistantTurnResponse {
  status: 'ok' | 'accepted' | 'blocked' | 'error';
  message: ChatMessage;
  budget?: AssistantBudget;
  meta?: {
    iterations: number;
    tool_calls: string[];
    stopped_reason: string;
    cost_eur: number;
  };
}

export async function getAssistantBudget(): Promise<AssistantBudget> {
  const { data } = await api.get<AssistantBudget>('/v1/assistant/budget');
  return data;
}

export async function postAssistantTurn(
  body: string,
  sourceUrl: string | null,
  locale: string | null,
  retryOfMessageId?: string | null,
  threadId?: string | null,
): Promise<AssistantTurnResponse> {
  // On a retry (#1790) the backend uses the persisted user message's body as
  // authoritative and does NOT insert a new user row — so exactly one user
  // message stays in the thread. ``retry_of_message_id`` is sent only when set;
  // ``body`` may be empty on a retry (the backend ignores it).
  // ``thread_id`` (FR-015) targets a specific conversation; omitted → the
  // backend resolves the most recently active one (or creates the first).
  const { data } = await api.post<AssistantTurnResponse>('/v1/assistant/turn', {
    body,
    source_url: sourceUrl,
    locale,
    ...(retryOfMessageId ? { retry_of_message_id: retryOfMessageId } : {}),
    ...(threadId ? { thread_id: threadId } : {}),
  });
  return data;
}

// ---------------------------------------------------------------------- //
// Agent workspace (FR-015): projects + conversations management.
// ---------------------------------------------------------------------- //
export async function listAssistantProjects(): Promise<ChatProject[]> {
  const { data } = await api.get<{ projects: ChatProject[] }>('/v1/assistant/projects');
  return data.projects;
}

export async function createAssistantProject(name: string): Promise<ChatProject> {
  const { data } = await api.post<ChatProject>('/v1/assistant/projects', { name });
  return data;
}

export async function renameAssistantProject(projectId: string, name: string): Promise<ChatProject> {
  const { data } = await api.patch<ChatProject>(`/v1/assistant/projects/${projectId}`, { name });
  return data;
}

/** Soft-deletes the project AND its conversations (FR-015 cascade). */
export async function deleteAssistantProject(
  projectId: string,
): Promise<{ project: ChatProject; deleted_thread_ids: string[] }> {
  const { data } = await api.delete<{ project: ChatProject; deleted_thread_ids: string[] }>(
    `/v1/assistant/projects/${projectId}`,
  );
  return data;
}

export async function createAssistantConversation(options?: {
  title?: string;
  projectId?: string | null;
}): Promise<ChatThread> {
  const { data } = await api.post<ChatThread>('/v1/assistant/conversations', {
    ...(options?.title ? { title: options.title } : {}),
    ...(options?.projectId ? { project_id: options.projectId } : {}),
  });
  return data;
}

/**
 * Partial update. Send exactly the provided keys — ``project_id: null`` is a
 * meaningful "detach from project" (≠ omitting the key).
 */
export async function updateAssistantConversation(
  threadId: string,
  patch: { title?: string; project_id?: string | null },
): Promise<ChatThread> {
  const { data } = await api.patch<ChatThread>(`/v1/assistant/conversations/${threadId}`, patch);
  return data;
}

export async function deleteAssistantConversation(threadId: string): Promise<ChatThread> {
  const { data } = await api.delete<ChatThread>(`/v1/assistant/conversations/${threadId}`);
  return data;
}

export async function listThreads(): Promise<ChatThread[]> {
  const { data } = await api.get<ChatThread[]>('/v1/chat/threads');
  return data;
}

export async function getMessages(threadId: string, limit = 50): Promise<ChatMessage[]> {
  const { data } = await api.get<ChatMessage[]>(`/v1/chat/threads/${threadId}/messages`, {
    params: { limit },
  });
  return data;
}

export async function postMessage(
  threadId: string,
  body: string,
  sourceUrl: string | null,
): Promise<ChatMessage> {
  const { data } = await api.post<ChatMessage>(`/v1/chat/threads/${threadId}/messages`, {
    body,
    source_url: sourceUrl,
  });
  return data;
}

export async function markRead(threadId: string, watermarkMessageId: string): Promise<void> {
  await api.post(`/v1/chat/threads/${threadId}/read`, {
    watermark_message_id: watermarkMessageId,
  });
}

/**
 * Clear (non-destructively hide) the user's own conversation (#1732).
 * Sets a server-side ``cleared_at`` watermark so subsequent reads hide
 * messages sent at/before it. No message is deleted — admin/admin-MCP
 * reads keep the full history. Returns the updated thread row.
 */
export async function clearAssistantThread(threadId: string): Promise<ChatThread> {
  const { data } = await api.post<ChatThread>(`/v1/chat/threads/${threadId}/clear`);
  return data;
}

export async function listAdminThreads(): Promise<AdminChatThread[]> {
  const { data } = await api.get<AdminChatThread[]>('/v1/chat/admin/threads');
  return data;
}

/**
 * Cross-tenant aggregated admin feed. Backed by
 * ``GET /v1/chat/admin/threads/all`` which fans out per-tenant queries
 * in parallel. Used by the admin left panel + the admin home unread
 * badge so a single farmer's support thread shows up regardless of
 * which tenant it belongs to.
 */
export async function listAdminThreadsAll(): Promise<AdminChatThread[]> {
  const { data } = await api.get<AdminChatThread[]>('/v1/chat/admin/threads/all');
  return data;
}

export async function adminEnsureThread(userId: string, threadKind = 'human_support'): Promise<ChatThread> {
  const { data } = await api.post<ChatThread>(
    '/v1/chat/admin/threads/ensure',
    null,
    { params: { user_id: userId, thread_kind: threadKind } },
  );
  return data;
}

export async function adminReply(
  threadId: string,
  body: string,
  tenantId?: string,
): Promise<ChatMessage> {
  const { data } = await api.post<ChatMessage>(
    `/v1/chat/admin/threads/${threadId}/reply`,
    { body },
    tenantId ? { params: { tenant_id: tenantId } } : undefined,
  );
  return data;
}

export async function adminDeleteMessage(messageId: string, tenantId?: string): Promise<void> {
  // #998: URL moved from /v1/chat/messages/{id} to /v1/chat/admin/messages/{id}
  // so the BFF's chat/admin/ allowlist catches the prefix (otherwise the
  // delete 403s during admin impersonation before _emit_audit runs).
  await api.delete(
    `/v1/chat/admin/messages/${messageId}`,
    tenantId ? { params: { tenant_id: tenantId } } : undefined,
  );
}

export async function adminMarkRead(
  threadId: string,
  watermarkMessageId: string,
  tenantId?: string,
): Promise<void> {
  await api.post(
    `/v1/chat/admin/threads/${threadId}/read`,
    { watermark_message_id: watermarkMessageId },
    tenantId ? { params: { tenant_id: tenantId } } : undefined,
  );
}

export async function adminGetMessages(
  threadId: string,
  tenantId: string,
  limit = 50,
): Promise<ChatMessage[]> {
  const { data } = await api.get<ChatMessage[]>(
    `/v1/chat/admin/threads/${threadId}/messages`,
    { params: { limit, tenant_id: tenantId } },
  );
  return data;
}
